import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { checkExportStatus, type ExportStatusResponse } from '../utils/exportService';
import { checkBulkActionStatus, type BulkActionStatusResponse } from '../utils/bulkActionService';
import {
  listNotifications,
  deleteNotification,
  markNotificationRead,
  markAllNotificationsRead,
  type NotificationRecord,
} from '../utils/notificationsService';
import {
  isImportJobSettled,
  type ImportJob,
  type ImportJobStatus,
} from '@charitystack/imports-contracts';
import { getImportJob } from '../api/imports';

/**
 * The kinds of work this panel tracks.
 *
 * `import` (PE-1911) is a contact import from the Import Hub. It is a
 * FIRST-CLASS type with its own poll branch, deliberately not folded into the
 * bulk-action fallback — see `pollActiveJobs` for why that distinction is
 * load-bearing rather than stylistic.
 */
export type JobType = 'export' | 'bulk-action' | 'notification' | 'import';

export interface Job {
  jobId: string;
  type: JobType;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  createdAt: number;
  completedAt?: number;
  error?: string;
  /**
   * FAILED because the job went silent, as judged by the backend at read time
   * (PE-1857) — not because a worker reported a failure.
   *
   * Load-bearing detail for anyone touching the poll loop: this arrives as a
   * normal FAILED status, so `isActiveJob` already goes false and the 3s
   * interval already stops. That is the whole fix for the infinite spinner;
   * this flag is provenance, and no rendering branches on it.
   */
  stale?: boolean;
  // Export-specific
  downloadUrl?: string;
  recordCount?: number;
  entityType?: string;
  // Bulk-action-specific
  actionType?: string;
  totalCount?: number;
  processedCount?: number;
  successCount?: number;
  failedCount?: number;
  /** CON-857: tax-documents ZIP contacts skipped for having no gifts (not failures). */
  skippedCount?: number;
  // Import-specific (PE-1911)
  /** The uploaded file's name, so the card names the import the user started. */
  fileName?: string;
  /**
   * The job's real §3.2 status (e.g. COMMITTING, SUCCESS, FAILED_NEEDS_CLEANUP).
   * `status` above is the panel's own four-state vocabulary, which every job
   * type is projected onto; this preserves the untruncated import state for
   * anything that needs to tell REVERSING from COMMITTING.
   */
  importStatus?: ImportJobStatus;
  /**
   * Polls remaining in the post-commit claim grace (PE-1911) — see
   * `MAX_IMPORT_CLAIM_GRACE_POLLS`. While above zero, a DRAFT status read back
   * from the server does NOT disarm the poll, because the workflow has not
   * necessarily claimed the job yet. Counts down on every poll and is dropped
   * the moment the job reads as genuinely claimed, so it can only ever extend
   * polling by a bounded number of ticks.
   */
  importClaimGracePolls?: number;
  // Notification-specific
  actionLabel?: string;
  userEmail?: string;
  isRead?: boolean;
  originalJobId?: string;
  s3Key?: string;
  // Optional in-app deep link. When a notification's metadata carries a
  // `navigationUrl`, clicking the card routes there (e.g. a contact-health
  // alert linking to the affected contacts). Server-provided so the FE
  // doesn't hard-code per-actionType routes.
  navigationUrl?: string;
}

interface JobsContextValue {
  jobs: Job[];
  activeCount: number;
  unreadCount: number;
  transitioningCount: number;
  addExportJob: (jobId: string, entityType: string, totalCount?: number) => void;
  addBulkActionJob: (jobId: string, actionType: string, totalCount?: number) => void;
  addImportJob: (jobId: string, fileName?: string) => void;
  removeJob: (jobId: string, type: JobType) => Promise<void>;
  markRead: (jobId: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  refreshJobs: () => void;
  isRefreshing: boolean;
  isInitializing: boolean;
  isPanelOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
}

const JobsContext = createContext<JobsContextValue | null>(null);

export function useJobs() {
  const context = useContext(JobsContext);
  if (!context) {
    throw new Error('useJobs must be used within a JobsProvider');
  }
  return context;
}

// Safe wrapper that returns null instead of throwing (for use in hooks)
export function useJobsOptional() {
  return useContext(JobsContext);
}

// Imperative access for class components — stores a reference to the latest context value
let _jobsContextRef: JobsContextValue | null = null;
export function getJobsContext(): JobsContextValue | null {
  return _jobsContextRef;
}

const POLL_INTERVAL = 3000;

/**
 * How many CONSECUTIVE failed status reads an import tolerates before the card
 * settles as FAILED and the poll stops (PE-1911).
 *
 * Ten at the 3s cadence is ~30s of retrying, which comfortably absorbs a token
 * refresh or a blip while still being bounded. The counter resets on any
 * successful read, so a flaky connection does not accumulate toward the cap.
 */
export const MAX_IMPORT_POLL_FAILURES = 10;

/**
 * How many polls a freshly-committed import may keep reading a DRAFT status
 * before the draft carve-out in `isActiveJob` applies to it (PE-1911).
 *
 * THE RACE THIS CLOSES. `imports.commit` returns BEFORE the workflow claims the
 * job, so the server legitimately still reads READY for a short window after
 * the wizard registers the card. READY is a draft status, and a draft is
 * excluded from `isActiveJob` — so without a grace window the very first poll
 * can write READY over the seeded COMMITTING, disarm the 3s interval for good,
 * and strand the card on "Draft import — not yet started" while the import
 * actually runs. The user then sees nothing move until PE-1912's durable
 * completion card lands, which reads as a hung import.
 *
 * Same shape as the Hub's `claimTicks` (`importHubModel.shouldPollJobs`), and
 * for the same reason: arm the poll on the user's INTENT to commit rather than
 * on a status that has not caught up yet. Bounding it is what keeps the draft
 * carve-out honest — a claim that never lands settles the card instead of
 * polling forever, so the unbounded-draft-poll hazard the carve-out exists to
 * prevent stays prevented.
 *
 * Five at the 3s cadence is ~15s, comfortably longer than a claim takes and far
 * shorter than the failure budget above.
 */
export const MAX_IMPORT_CLAIM_GRACE_POLLS = 5;

/**
 * Whether a row is tracked CLIENT-side (added by an action in this tab and
 * polled here) rather than fetched from the notifications table.
 *
 * Defined once, as the complement of `notification`, because the alternative —
 * enumerating the local types at each call site — is what let `import` be
 * silently dropped from two filters when it was added. Anything that is not a
 * server-side notification is, by construction, something this context is
 * holding on the client.
 */
function isLocallyTrackedJob(job: Job): boolean {
  return job.type !== 'notification';
}

/**
 * Whether a job is work the poller should be watching.
 *
 * PENDING/PROCESSING is the general rule, with one carve-out: a DRAFT import
 * (STAGED / MAPPING / VALIDATING / READY) is PENDING because it is unfinished,
 * but it is waiting on the USER, not on the backend. Counting it as active
 * would arm the 3s interval with no way to disarm it — the poll deliberately
 * skips drafts, so nothing it could read would ever change the status, and the
 * timer would stay live for the tab's lifetime.
 *
 * This is reachable, not theoretical: a commit that fails before the workflow
 * claims the job leaves it in READY by design, and READY → MAPPING →
 * VALIDATING → READY is a legal re-validation cycle.
 *
 * The carve-out is SUSPENDED while a claim grace is outstanding
 * (`importClaimGracePolls`). A just-committed job legitimately still reads
 * READY until the workflow claims it, and treating that as a settled draft is
 * what would disarm the poll on the one job the user is actually waiting for.
 * The grace is bounded, so the carve-out reasserts itself either way.
 */
function isActiveJob(job: Job): boolean {
  if (job.status !== 'PENDING' && job.status !== 'PROCESSING') return false;
  if (job.type === 'import' && job.importStatus) {
    if ((job.importClaimGracePolls ?? 0) > 0) return true;
    return !isImportDraftStatus(job.importStatus);
  }
  return true;
}

function mapExportJob(job: ExportStatusResponse): Job {
  return {
    jobId: job.jobId,
    type: 'export',
    status: job.status,
    createdAt: job.createdAt,
    completedAt: job.completedAt,
    downloadUrl: job.downloadUrl,
    recordCount: job.recordCount,
    entityType: job.entityType,
    error: job.error,
    processedCount: job.processedCount,
    totalCount: job.totalCount,
    successCount: job.successCount,
    failedCount: job.failedCount,
    skippedCount: job.skippedCount,
    stale: job.stale,
  };
}

function mapBulkActionJob(job: BulkActionStatusResponse): Job {
  return {
    jobId: job.jobId,
    type: 'bulk-action',
    status: job.status,
    createdAt: job.createdAt,
    completedAt: job.completedAt,
    actionType: job.actionType,
    totalCount: job.totalCount,
    processedCount: job.processedCount,
    successCount: job.successCount,
    failedCount: job.failedCount,
    error: job.error,
    stale: job.stale,
  };
}

/**
 * Projects an import job's §3.2 status onto the panel's four-state vocabulary
 * (PE-1911).
 *
 * The panel speaks PENDING/PROCESSING/COMPLETED/FAILED; imports have thirteen
 * states, so this is a genuine narrowing and each bucket is assigned by name.
 *
 * ⚠️ `isImportJobSettled` ALONE IS THE WRONG QUESTION. It answers "should the
 * poller stop?", and it is `true` for the four pre-commit DRAFT states
 * (STAGED / MAPPING / VALIDATING / READY) — nothing moves those without the
 * user, so the poller correctly rests. But "stopped moving" is not "finished":
 * projecting a draft to COMPLETED renders a green check reading "0 contacts
 * imported" for an import that never ran. That is reachable in production, not
 * hypothetical — a commit that fails before the workflow claims the job leaves
 * it in READY by design, and `READY → MAPPING → VALIDATING → READY` is a legal
 * re-validation cycle.
 *
 * So the three questions are kept separate:
 *   - working (COMMITTING/CANCELLING/REVERSING) → PROCESSING, keep polling.
 *   - draft (the four pre-commit states)        → PENDING, an unfinished job
 *     that is waiting on the USER. PENDING would normally arm the poll, which
 *     is why `pollActiveJobs` skips drafts explicitly — see there.
 *   - finished                                  → COMPLETED / FAILED by name.
 */
const IMPORT_FAILURE_STATUSES: ReadonlySet<ImportJobStatus> = new Set([
  'FAILED',
  'FAILED_NEEDS_CLEANUP',
]);

/**
 * The pre-commit states of §3.2 — a job the user is still assembling, which has
 * imported nothing and claims nothing.
 */
const IMPORT_DRAFT_STATUSES: ReadonlySet<ImportJobStatus> = new Set([
  'STAGED',
  'MAPPING',
  'VALIDATING',
  'READY',
]);

export function isImportDraftStatus(status: ImportJobStatus): boolean {
  return IMPORT_DRAFT_STATUSES.has(status);
}

export function panelStatusForImport(status: ImportJobStatus): Job['status'] {
  if (!isImportJobSettled(status)) return 'PROCESSING';
  if (IMPORT_DRAFT_STATUSES.has(status)) return 'PENDING';
  return IMPORT_FAILURE_STATUSES.has(status) ? 'FAILED' : 'COMPLETED';
}

function mapImportJob(job: ImportJob): Job {
  const counts = job.counts;
  return {
    jobId: job.jobId,
    type: 'import',
    status: panelStatusForImport(job.status),
    importStatus: job.status,
    // The panel's timestamps are epoch SECONDS; the contract's `createdAt` is
    // an ISO-8601 string (§3.1). Converting here keeps `formatTimeAgo` and the
    // list sort working without special-casing imports downstream.
    createdAt: Math.floor(new Date(job.createdAt).getTime() / 1000),
    fileName: job.fileName,
    // §3.1's counts map. `created + merged` is what the user reads as "landed";
    // the buckets themselves stay available for the card's detail line.
    totalCount: counts?.total,
    processedCount:
      counts === undefined
        ? undefined
        : counts.created + counts.merged + counts.excluded + counts.errored,
    successCount: counts === undefined ? undefined : counts.created + counts.merged,
    failedCount: counts?.errored,
  };
}

/**
 * Drops a locally-tracked job once the durable notification for the SAME job
 * has arrived.
 *
 * The two records describe one event but share no id: PE-1912's notification
 * gets a fresh UUID and carries the import's id in `metadata.jobId`, which
 * `mapNotification` surfaces as `originalJobId`. Correlating on that is what
 * prevents the user seeing "Importing donors.csv" beside "Contact import
 * complete — 1,204 new".
 *
 * The notification WINS: it is the durable, server-side record that survives a
 * reload, and it carries the finished counts. The local row exists only to
 * cover the window before it appears.
 *
 * Import ids are additionally qualified by `CONTACT_IMPORT`: exports and bulk
 * actions also put their job id in `metadata.jobId`, so an unrelated producer
 * must never be allowed to retire a live import card. For the other local job
 * types the globally unique job id is the correlation contract.
 */
const IMPORT_NOTIFICATION_ACTION_TYPE = 'CONTACT_IMPORT';

export function reconcileLocalJobCards(jobs: Job[]): Job[] {
  const notificationsByOriginalJobId = new Map(
    jobs
      .filter((j) => j.type === 'notification' && j.originalJobId)
      .map((j) => [j.originalJobId as string, j] as const),
  );
  if (notificationsByOriginalJobId.size === 0) return jobs;

  return jobs.filter((job) => {
    if (!isLocallyTrackedJob(job)) return true;
    const notification = notificationsByOriginalJobId.get(job.jobId);
    if (!notification) return true;
    if (job.type === 'import') {
      return notification.actionType !== IMPORT_NOTIFICATION_ACTION_TYPE;
    }
    return false;
  });
}

/** Avoid notifications API during signup/onboarding — token may not be org-scoped yet (401 spam). */
function shouldSkipNotificationsFetch(pathname: string): boolean {
  return (
    pathname.includes('/onboarding') ||
    pathname.includes('/signup') ||
    pathname.includes('/signin') ||
    pathname.includes('/request-access')
  );
}

function mapNotification(notification: NotificationRecord): Job {
  return {
    jobId: notification.id,
    type: 'notification',
    status: notification.status as Job['status'],
    createdAt: notification.createdAt,
    actionType: notification.actionType,
    entityType: notification.entityType,
    actionLabel: notification.actionLabel,
    userEmail: notification.userEmail,
    isRead: notification.isRead ?? false,
    recordCount: notification.metadata?.recordCount,
    successCount: notification.metadata?.successCount,
    failedCount: notification.metadata?.failedCount,
    originalJobId: notification.metadata?.jobId,
    s3Key: notification.metadata?.s3Key,
    navigationUrl: notification.metadata?.navigationUrl,
    // PE-1766 — SMS_NUMBER_PROVISIONING rejection cards surface PF's verbatim
    // reason via the FAILED card's existing error line. Generic
    // metadata.error kept as the fallback for other producers.
    error: notification.metadata?.rejectionReason ?? notification.metadata?.error,
  };
}

export function JobsProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [transitioningCount, setTransitioningCount] = useState(0);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const initializedRef = useRef(false);
  const prevActiveCountRef = useRef(0);

  const activeCount = jobs.filter(isActiveJob).length;

  const unreadCount = jobs.filter((j) => j.type === 'notification' && !j.isRead).length;

  // Load jobs from notifications table (single source of truth).
  // Returns true if auth was ready and load was attempted.
  const loadJobs = useCallback(async (): Promise<boolean> => {
    try {
      if (shouldSkipNotificationsFetch(location.pathname)) {
        setJobs((prev) => prev.filter(isLocallyTrackedJob));
        return true;
      }
      // Check if auth provider is initialized before attempting token fetch
      // (avoids noisy "Clerk not initialized" errors during startup race)
      const { isAuthReady, getAuthToken } = await import('../utils/auth');
      if (!isAuthReady()) return false; // Auth provider not mounted yet

      const token = await getAuthToken();
      if (!token) return false; // Auth not ready yet

      const notifications = await listNotifications().catch(() => [] as NotificationRecord[]);
      const notificationJobs = notifications.map(mapNotification);
      notificationJobs.sort((a, b) => b.createdAt - a.createdAt);

      setJobs((prev) => {
        // Preserve local cards through the server-notification handoff. Fast
        // jobs can complete before their durable notification is queryable;
        // dropping the local card here leaves Activity visibly empty on the
        // user's first attempt. Reconciliation removes it only after the
        // matching durable card actually arrives.
        const localJobs = prev.filter(isLocallyTrackedJob);
        return reconcileLocalJobCards([...localJobs, ...notificationJobs]);
      });
      return true;
    } catch (err) {
      console.error('Failed to load jobs:', err);
      return false;
    }
  }, [location.pathname]);

  // When user leaves onboarding/signup for the main app, load notifications once.
  const wasOnSkipPathRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    const skip = shouldSkipNotificationsFetch(location.pathname);
    if (wasOnSkipPathRef.current === true && skip === false) {
      loadJobs();
    }
    wasOnSkipPathRef.current = skip;
  }, [location.pathname, loadJobs]);

  // Keep a ref to current jobs so pollActiveJobs can read without depending on state
  const jobsRef = useRef<Job[]>([]);
  jobsRef.current = jobs;

  // jobId → consecutive failed status reads, for the import poll's give-up cap.
  // A ref (not state) because changing it must never trigger a render — it is
  // bookkeeping for the interval, not something the UI displays.
  const importFailureCountsRef = useRef<Map<string, number>>(new Map());

  // Poll active jobs for status updates
  const pollActiveJobs = useCallback(async () => {
    const currentJobs = jobsRef.current;
    // Same predicate the interval is armed on, so "what counts as active" is
    // decided in exactly one place.
    const activeJobs = currentJobs.filter(isActiveJob);

    if (activeJobs.length === 0) return;

    // Fire off status checks for each active job.
    //
    // ⚠️ EVERY BRANCH HERE IS EXPLICIT ON `job.type`, AND THE FALLBACK IS
    // "DON'T POLL" (PE-1911). This used to read `if (export) … else
    // checkBulkActionStatus(…)`, so ANY job type that wasn't `export` was
    // polled against the bulk-action endpoint. A type that endpoint doesn't
    // know 404s, the `catch` below swallows it, the job stays
    // PENDING/PROCESSING forever, and this 3s interval never stops — a silent
    // unbounded background poll against the wrong service.
    //
    // Returning the job untouched for an unrecognized type is the safe
    // default: it keeps its current state and the ACTIVE-count effect is what
    // decides whether to keep polling. Do not restore a catch-all `else`.
    const updatedActiveJobs = await Promise.all(
      activeJobs.map(async (job) => {
        try {
          if (job.type === 'export') {
            const status = await checkExportStatus(job.jobId);
            return mapExportJob(status);
          }
          if (job.type === 'bulk-action') {
            const status = await checkBulkActionStatus(job.jobId);
            return mapBulkActionJob(status);
          }
          if (job.type === 'import') {
            // Drafts never reach here — `isActiveJob` excludes them, which is
            // also what keeps them from arming this interval at all.
            // `jobs.get` returns `{ job }`, not the job itself.
            const { job: importJob } = await getImportJob({ jobId: job.jobId });
            importFailureCountsRef.current.delete(job.jobId);
            const mapped = mapImportJob(importJob);
            // Carry the claim grace across the remap (mapImportJob builds a
            // fresh object from the server's view, which has no notion of it).
            // A job that no longer reads as a draft IS claimed, so the grace
            // has done its job and is dropped rather than counted down — that
            // keeps it a startup allowance and not a general poll extension.
            if (!isImportDraftStatus(mapped.importStatus ?? 'READY')) return mapped;
            const graceLeft = (job.importClaimGracePolls ?? 0) - 1;
            return graceLeft > 0 ? { ...mapped, importClaimGracePolls: graceLeft } : mapped;
          }
          // 'notification' rows are never active (their producers only ever
          // emit terminal cards), and an unknown type has no endpoint. Neither
          // is pollable — leave it exactly as it is.
          return job;
        } catch (err) {
          // ⚠️ A SWALLOWED ERROR MUST NOT MEAN "POLL FOREVER".
          //
          // Returning the job unchanged keeps it PENDING/PROCESSING, which
          // keeps `activeCount` above zero, which keeps this 3s interval
          // running — so a read that fails EVERY time (403 after an org
          // switch, a TTL-expired job record, a network partition) becomes a
          // permanent background request stream with no UI signal. That is the
          // same defect class the routing fix above addressed, arriving by a
          // different door.
          //
          // Imports therefore give up after a bounded run of consecutive
          // failures and settle the card as FAILED. Exports and bulk actions
          // keep the original keep-current-state behaviour: this is a targeted
          // fix for the type being added, not a silent change to how existing
          // job types report transient errors.
          if (job.type === 'import') {
            const failures = (importFailureCountsRef.current.get(job.jobId) ?? 0) + 1;
            importFailureCountsRef.current.set(job.jobId, failures);
            if (failures >= MAX_IMPORT_POLL_FAILURES) {
              importFailureCountsRef.current.delete(job.jobId);
              // Reported ONCE, at settle — not on every failing tick. Ten
              // transient blips that recover are noise; a run that exhausts
              // the budget and flips the card to FAILED is the signal worth a
              // Sentry event (the intermediate errors were all swallowed by
              // design, so this is the only place the failure surfaces at all).
              console.error('Import status poll gave up after repeated failures', err);
              Sentry.captureException(err, {
                tags: {
                  feature: 'import-tool',
                  job_id: job.jobId,
                  step: 'status-poll',
                },
              });
              return {
                ...job,
                status: 'FAILED' as const,
                error:
                  'Could not read this import’s status. Check Settings › Imports for the current state.',
              };
            }
          }
          return job; // Keep current state on error
        }
      }),
    );

    // Merge updated jobs into state; detect genuinely new completions
    // by comparing previous status inside the updater to avoid double-counting
    // if a poll fires before the prior state update renders.
    setJobs((prevJobs) => {
      const updatedMap = new Map(updatedActiveJobs.map((j) => [j.jobId, j]));
      let newlyCompletedCount = 0;
      const nextJobs = prevJobs.map((j) => {
        const updated = updatedMap.get(j.jobId);
        if (
          updated &&
          (updated.status === 'COMPLETED' || updated.status === 'FAILED') &&
          j.status !== 'COMPLETED' &&
          j.status !== 'FAILED'
        ) {
          newlyCompletedCount++;
        }
        return updated || j;
      });
      if (newlyCompletedCount > 0) {
        setTransitioningCount((prev) => prev + newlyCompletedCount);
      }
      return nextJobs;
    });
  }, []);

  // Start/stop polling based on active jobs
  useEffect(() => {
    if (activeCount > 0) {
      if (!pollTimerRef.current) {
        pollTimerRef.current = setInterval(pollActiveJobs, POLL_INTERVAL);
      }
    } else {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      // When all active jobs finish, re-fetch notifications. Keep the completed
      // local card until that fetch actually contains the durable replacement;
      // otherwise a fast job creates an empty Activity-panel gap.
      if (prevActiveCountRef.current > 0) {
        setIsRefreshing(true);
        loadJobs().finally(() => {
          setIsRefreshing(false);
          setTransitioningCount(0);
        });
      }
    }
    prevActiveCountRef.current = activeCount;

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [activeCount, pollActiveJobs, loadJobs]);

  // Initialize on mount — retry with backoff until auth is ready
  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      let attempt = 0;
      const maxAttempts = 5;
      const delays = [1000, 2000, 3000, 4000, 5000];
      let cancelled = false;
      let retryTimer: ReturnType<typeof setTimeout> | null = null;

      const tryLoad = async () => {
        const loaded = await loadJobs();
        if (loaded) {
          setIsInitializing(false);
        } else if (attempt < maxAttempts && !cancelled) {
          attempt++;
          retryTimer = setTimeout(tryLoad, delays[attempt - 1]);
        } else {
          setIsInitializing(false);
        }
      };

      tryLoad();

      return () => {
        cancelled = true;
        if (retryTimer) clearTimeout(retryTimer);
      };
    }
  }, [loadJobs]);

  const addExportJob = useCallback((jobId: string, entityType: string, totalCount?: number) => {
    const newJob: Job = {
      jobId,
      type: 'export',
      status: 'PENDING',
      createdAt: Math.floor(Date.now() / 1000),
      entityType,
      recordCount: 0,
      totalCount: totalCount || 0,
      processedCount: 0,
    };
    setJobs((prev) => [newJob, ...prev]);
  }, []);

  const addBulkActionJob = useCallback((jobId: string, actionType: string, totalCount?: number) => {
    const newJob: Job = {
      jobId,
      type: 'bulk-action',
      status: 'PENDING',
      createdAt: Math.floor(Date.now() / 1000),
      actionType,
      totalCount: totalCount || 0,
      processedCount: 0,
      successCount: 0,
      failedCount: 0,
    };
    setJobs((prev) => [newJob, ...prev]);
  }, []);

  /**
   * Register a contact import that has just been committed (PE-1911).
   *
   * Called from the wizard's commit path so the bell shows the running import
   * immediately, rather than only once it finishes. Seeded as PROCESSING (not
   * PENDING): by the time the wizard calls this, `commitImportJob` has already
   * returned, so the job is genuinely COMMITTING server-side.
   *
   * On completion PE-1912's SQS producer writes the durable bell card. The
   * local row is dropped by the activeCount→0 cleanup below, which then
   * re-fetches notifications — so the user ends up with that one card, not a
   * stale local duplicate beside it.
   */
  const addImportJob = useCallback((jobId: string, fileName?: string) => {
    // Clear any failure tally from an earlier run under this id. The wizard
    // reuses `jobIdRef` until a commit succeeds, so a retried import can arrive
    // here with the same id — inheriting a stale count would make a healthy
    // import give up after one or two reads.
    importFailureCountsRef.current.delete(jobId);
    const newJob: Job = {
      jobId,
      type: 'import',
      status: 'PROCESSING',
      importStatus: 'COMMITTING',
      // The workflow claims the job asynchronously, so the next few reads may
      // still return READY. Without this grace the first such read would look
      // like a settled draft and stop the poll — see
      // MAX_IMPORT_CLAIM_GRACE_POLLS.
      importClaimGracePolls: MAX_IMPORT_CLAIM_GRACE_POLLS,
      createdAt: Math.floor(Date.now() / 1000),
      fileName,
    };
    setJobs((prev) => [newJob, ...prev]);
  }, []);

  const removeJob = useCallback(async (jobId: string, type: JobType) => {
    try {
      if (type === 'notification') {
        await deleteNotification(jobId);
      }
      // For export/bulk-action types (locally-added active jobs), just remove from state
      setJobs((prev) => prev.filter((j) => j.jobId !== jobId));
    } catch (err) {
      console.error('Failed to delete job:', err);
      throw err;
    }
  }, []);

  const markRead = useCallback(async (jobId: string) => {
    try {
      await markNotificationRead(jobId);
      setJobs((prev) => prev.map((j) => (j.jobId === jobId ? { ...j, isRead: true } : j)));
    } catch (err) {
      console.error('Failed to mark notification read:', err);
      throw err;
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await markAllNotificationsRead();
      setJobs((prev) => prev.map((j) => (j.type === 'notification' ? { ...j, isRead: true } : j)));
    } catch (err) {
      console.error('Failed to mark all notifications read:', err);
      throw err;
    }
  }, []);

  const openPanel = useCallback(() => {
    setIsPanelOpen(true);
    // Opening Activity is a natural retry point for the durable handoff. If a
    // completion notification was still propagating during the first fetch,
    // this replaces the temporary local card without requiring a reload.
    setIsRefreshing(true);
    loadJobs().finally(() => setIsRefreshing(false));
  }, [loadJobs]);
  const closePanel = useCallback(() => setIsPanelOpen(false), []);

  const refreshJobs = useCallback(() => {
    loadJobs();
  }, [loadJobs]);

  const contextValue: JobsContextValue = useMemo(
    () => ({
      jobs,
      activeCount,
      unreadCount,
      transitioningCount,
      addExportJob,
      addBulkActionJob,
      addImportJob,
      removeJob,
      markRead,
      markAllRead,
      refreshJobs,
      isRefreshing,
      isInitializing,
      isPanelOpen,
      openPanel,
      closePanel,
    }),
    [
      jobs,
      activeCount,
      unreadCount,
      transitioningCount,
      addExportJob,
      addBulkActionJob,
      addImportJob,
      removeJob,
      markRead,
      markAllRead,
      refreshJobs,
      isRefreshing,
      isInitializing,
      isPanelOpen,
      openPanel,
      closePanel,
    ],
  );

  // Keep imperative ref in sync for class component access via getJobsContext().
  // Assigned in useEffect so it only updates after a committed render
  // (avoids stale refs from abandoned renders in React 18 Concurrent Mode).
  useEffect(() => {
    _jobsContextRef = contextValue;
    return () => {
      _jobsContextRef = null;
    };
  }, [contextValue]);

  return <JobsContext.Provider value={contextValue}>{children}</JobsContext.Provider>;
}
